/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useTranslation } from 'react-i18next';
import { StarIcon } from '@/components/product-ratings';
import type { ProductReviews } from '@/extensions/nemesis/lib/reviews.server';

/**
 * Compact ratings summary sourced from the head-side reviews vendor integration seam
 * (`@/extensions/nemesis/lib/reviews.server`) — e.g. "★ 4.3 (128)". Distinct from, and
 * visually separate from, the `ratings-reviews` extension's above-the-fold `ProductRatingSummary`.
 * Renders nothing when the vendor has no reviews for this product.
 */
export function IntegrationReviewsSummary({ reviews }: { reviews: ProductReviews }) {
    const { t } = useTranslation('product');
    if (reviews.totalCount === 0) {
        return null;
    }
    return (
        <p className="mt-16 flex items-center gap-1 text-sm text-muted-foreground">
            <StarIcon opacity={1} filled aria-hidden="true" className="h-3 w-3" />
            {t('rating.ratingWithCount', {
                rating: Number(reviews.averageRating.toFixed(1)).toString(),
                count: reviews.totalCount,
            })}
        </p>
    );
}

/** Skeleton fallback for `IntegrationReviewsSummary` — below the fold, so kept minimal. */
export function IntegrationReviewsSummarySkeleton() {
    return <div className="mt-16 h-4 w-24 animate-pulse rounded bg-muted" aria-hidden="true" />;
}
